import { Mail, MessageCircle, Github, Linkedin, ArrowUpRight } from "lucide-react";
import { IDS } from "@/constants/testIds";
import { OWNER } from "@/data/content";
import { ConnectDialog } from "@/components/ConnectDialog";

export default function ConnectSection() {
  return (
    <section id="connect" className="border-b border-border py-24 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-10 left-10 h-40 w-40 rounded-full opacity-30 blur-3xl bg-[#E3FF00]/40"></div>

      <div className="mx-auto max-w-7xl px-4 sm:px-8 relative">
        <div className="flex items-center gap-3 font-jet text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
          <span>05</span><span className="h-px w-8 bg-border"></span><span>Connect</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <h2 className="font-mono-brut font-black uppercase text-5xl sm:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
              got an idea?
              <br />
              <span className="text-[#E3FF00]">let&apos;s ship it.</span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-foreground/70 max-w-2xl">
              Pick your poison — I reply within 9 hours. Every message gets a real human
              response, not a canned auto-reply.
            </p>
          </div>

          <div className="md:col-span-4 flex md:justify-end">
            <ConnectDialog
              triggerLabel={<>Start the convo →</>}
              triggerClassName="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#E3FF00] text-[#0A0A0A] px-8 py-5 font-mono-brut font-black uppercase text-base tracking-widest brut-shadow-sm hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0_0_#F4F4F0] transition-all"
              source="connect-section"
            />
          </div>
        </div>

        {/* Direct channel tiles */}
        <div className="grid grid-cols-1 sm:grid-cols-3 mt-14 border border-border">
          <a
            data-testid={IDS.footer.email}
            href={`mailto:${OWNER.email}`}
            className="group p-6 sm:p-8 border-b sm:border-b-0 sm:border-r border-border hover:bg-[#1F1F1F] transition"
          >
            <Mail size={18} className="text-[#E3FF00]" />
            <div className="mt-4 font-jet text-[10px] uppercase tracking-widest text-muted-foreground">Direct email</div>
            <div className="mt-2 font-mono-brut text-sm sm:text-base font-bold group-hover:text-[#E3FF00] transition flex items-center gap-2 break-all">
              {OWNER.email}
              <ArrowUpRight size={14} className="shrink-0" />
            </div>
          </a>
          <a
            data-testid={IDS.footer.whatsapp}
            href={`https://wa.me/${OWNER.whatsapp}`}
            target="_blank"
            rel="noreferrer"
            className="group p-6 sm:p-8 border-b sm:border-b-0 sm:border-r border-border hover:bg-[#1F1F1F] transition"
          >
            <MessageCircle size={18} className="text-[#E3FF00]" />
            <div className="mt-4 font-jet text-[10px] uppercase tracking-widest text-muted-foreground">WhatsApp</div>
            <div className="mt-2 font-mono-brut text-sm sm:text-base font-bold group-hover:text-[#E3FF00] transition flex items-center gap-2">
              {OWNER.whatsappDisplay}
              <ArrowUpRight size={14} />
            </div>
          </a>
          <a
            data-testid={IDS.footer.github}
            href={OWNER.socials.github}
            target="_blank"
            rel="noreferrer"
            className="group p-6 sm:p-8 hover:bg-[#1F1F1F] transition"
          >
            <Github size={18} className="text-[#E3FF00]" />
            <div className="mt-4 font-jet text-[10px] uppercase tracking-widest text-muted-foreground">GitHub</div>
            <div className="mt-2 font-mono-brut text-sm sm:text-base font-bold group-hover:text-[#E3FF00] transition flex items-center gap-2">
              @{OWNER.githubUsername}
              <ArrowUpRight size={14} />
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer data-testid={IDS.footer.root} className="py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="font-jet text-[11px] uppercase tracking-widest text-muted-foreground">
          © {new Date().getFullYear()} {OWNER.name} — Built with unreasonable care.
        </div>
        <div className="flex items-center gap-4 font-jet text-[11px] uppercase tracking-widest">
          <a href={OWNER.socials.github} target="_blank" rel="noreferrer" className="hover:text-[#E3FF00] inline-flex items-center gap-1">
            <Github size={12} /> GitHub
          </a>
          <a href={OWNER.socials.linkedin} target="_blank" rel="noreferrer" className="hover:text-[#E3FF00] inline-flex items-center gap-1">
            <Linkedin size={12} /> LinkedIn
          </a>
          <a href={`mailto:${OWNER.email}`} className="hover:text-[#E3FF00] inline-flex items-center gap-1">
            <Mail size={12} /> Email
          </a>
        </div>
      </div>
    </footer>
  );
}
